import { useEffect, useState } from 'react';
import { getAllTrainings, createTraining, updateTraining, getAllClasses } from '../../../api/training';
import { toast } from 'react-toastify';
import axiosInstance from '../../../api/axiosInstance';
import { getUserInfoDetails } from '../../../utlis';
import TrainingsHeader from './components/TrainingsHeader';
import TrainingsTable from './components/TrainingsTable';
import TrainingsModal from './components/TrainingsModal';

const Trainings = () => {
  const initialTraining = {
    candidate_id: '',
    class_id: '',
    status: 'ENROLLED',
    rating: 0,
    feedback: '',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [currentTraining, setCurrentTraining] = useState(initialTraining);
  const [trainingsList, setTrainingsList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [accessDenied, setAccessDenied] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [classes, setClasses] = useState([]);

  const openModalForNewTraining = () => {
    setIsEditMode(false);
    setEditingRecordId(null);
    setCurrentTraining(initialTraining);
    setIsModalOpen(true);
  };

  const openModalForUpdate = (recordId) => {
    const trainingData = trainingsList.find(
      (t) => t.record_id === recordId || t.id === recordId
    );
    if (!trainingData) {
      toast.error('Training record not found');
      return;
    }

    // Training data prepared for edit

    setIsEditMode(true);
    setEditingRecordId(trainingData.record_id || trainingData.id);
    setCurrentTraining({
      candidate_id: trainingData.candidate_id || '',
      class_id: trainingData.class_id || '',
      status: trainingData.status || 'ENROLLED',
      rating: trainingData.rating || 0,
      feedback: trainingData.feedback || '',
      // Display-only fields (not editable)
      created: trainingData.created || '',
      created_by: trainingData.created_by || '',
      updated: trainingData.updated || '',
      updated_by: trainingData.updated_by || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingRecordId(null);
    setCurrentTraining(initialTraining);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Form data received

      // Helper to parse integer safely
      const toInteger = (value) => {
        if (!value && value !== 0) return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      // Build training data matching API schema (camelCase field names)
      const trainingData = {
        status: formData.status,
      };

      // Add required fields (camelCase for API)
      const candidateId = toInteger(formData.candidate_id);
      if (candidateId !== null) trainingData.candidateId = candidateId;

      const classId = toInteger(formData.class_id);
      if (classId !== null) trainingData.classId = classId;

      // Add optional fields only if they have values
      const rating = toInteger(formData.rating);
      if (rating !== null && rating > 0) trainingData.rating = rating;

      if (formData.feedback?.trim()) trainingData.feedback = formData.feedback.trim();

      // Training data prepared for API

      if (isEditMode) {
        await updateTraining(editingRecordId, trainingData);
        toast.success('Training Updated Successfully');
      } else {
        await createTraining(trainingData);
        toast.success('Training Created Successfully');
      }

      closeModal();
      getData();
    } catch (error) {
      console.error('Error saving training:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        toast.error('Access denied: You do not have permission to manage trainings');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 400) {
        toast.error(errorMessage || 'Invalid data. Please check your input');
      } else {
        toast.error(errorMessage || 'Failed to save training');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await getAllTrainings(1, 100);
      // Trainings data fetched

      if (response && response.data) {
        const data = response.data;
        // Handle structure: { message, data: { totalRecords, pageNum, pageSize, records } }
        const records = data.data?.records || data.records || [];
        // Trainings list extracted
        setTrainingsList(Array.isArray(records) ? records : []);
      } else if (Array.isArray(response)) {
        setTrainingsList(response);
      } else {
        setTrainingsList([]);
      }
    } catch (error) {
      console.error('Error fetching trainings:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      // Check for 403 Forbidden - show on UI instead of toast
      if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        setAccessDenied(true);
      } else if (error.message === 'Network Error' || !error.response) {
        setAccessDenied(true);
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load trainings');
      }
      setTrainingsList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const { clientId, companyId } = getUserInfoDetails();
      const res = await axiosInstance.get('/candidates', {
        params: { company: companyId, client: clientId, pageNum: 1, pageSize: 500, status: 'Active' },
      });
      const data = res.data;
      const records = data?.data?.records || data?.records || [];
      setCandidates(records);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setCandidates([]);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await getAllClasses(1, 500);
      const data = res.data;
      const records = data?.data?.records || data?.records || [];
      const enrollmentClasses = records.filter((c) => c.status === 'ENROLLMENT');
      setClasses(enrollmentClasses);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setClasses([]);
    }
  };

  useEffect(() => {
    getData();
    fetchCandidates();
    fetchClasses();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter trainings based on search term
  const filteredTrainings = trainingsList.filter((trainingItem) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      trainingItem.candidate_name?.toLowerCase().includes(searchLower) ||
      trainingItem.class_name?.toLowerCase().includes(searchLower) ||
      trainingItem.status?.toLowerCase().includes(searchLower) ||
      trainingItem.feedback?.toLowerCase().includes(searchLower) ||
      String(trainingItem.record_id || '').includes(searchLower)
    );
  });

  // Show access denied screen
  if (accessDenied) {
    return (
      <div className="p-5">
        <div className="border border-gray-300 rounded-lg">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You are not authorized to manage trainings. Please contact your Admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header with actions */}
      <div className="border border-gray-300 rounded-lg relative">
        <TrainingsHeader
          onReload={getData}
          onAddTraining={openModalForNewTraining}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <TrainingsTable
            data={filteredTrainings}
            onEditTraining={openModalForUpdate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Trainings Modal */}
      <TrainingsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentTraining}
        isSubmitting={isSubmitting}
        candidates={candidates}
        classes={classes}
      />
    </div>
  );
};

export default Trainings;
